import type { UseFormReturn } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import type { CreateStartupFormValues } from '../../../schema';
import { FIELD_HELPERS, FIELD_LABELS, SECTION_TITLES } from '../../../constants';
import { TextField } from '@/components/forms/TextField';
import { FileUploadField } from '@/components/common/FileUploadField';

interface SectionProps {
  form: UseFormReturn<CreateStartupFormValues>;
  /**
   * Per-control tracker, NOT one shared handler: this card carries two uploads,
   * and with a single callback the first to finish would report "not uploading"
   * while the second was still running - reopening the very window that loses a
   * file.
   */
  trackUpload?: (id: string) => (isUploading: boolean) => void;
}

/** Step 1 "Uploads and Links" card (Figma 287:3098): three dropzones. */
export function UploadsLinks({ form, trackUpload }: SectionProps) {
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = form;
  // `logoFileKey` is the WRITE field - the raw S3 key the upload produces, and
  // the only logo field the Backend accepts on create (MYN-175). `logoUrl` is
  // the READ field: the Backend-resolved display URL for that same object, so
  // a hydrated draft still shows the done state. Mirrors the split already
  // shipped for the Trusted Partner profile (profiles/modules/partner).
  const hasLogo = Boolean(watch('logoFileKey') || watch('logoUrl'));
  const hasPitchDeck = Boolean(watch('pitchDeckFileKey'));

  return (
    <SectionCard title={SECTION_TITLES.UPLOADS_LINKS}>
      <div className="flex flex-col gap-5">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <FileUploadField
            label={FIELD_LABELS.logo}
            hint={FIELD_HELPERS.logo}
            kind="logo"
            accept="image/png,image/jpeg"
            uploaded={hasLogo}
            onUploaded={(result) => setValue('logoFileKey', result.fileKey, { shouldDirty: true })}
            // Both, or Remove cannot clear a done state that a hydrated
            // `logoUrl` is holding up on its own.
            onUploadingChange={trackUpload?.('logo')}
            onClear={() => {
              setValue('logoFileKey', '', { shouldDirty: true });
              setValue('logoUrl', '', { shouldDirty: true });
            }}
          />
          <FileUploadField
            label={FIELD_LABELS.pitchDeck}
            hint={FIELD_HELPERS.pitchDeck}
            kind="pitchDeck"
            accept="application/pdf"
            uploaded={hasPitchDeck}
            error={errors.pitchDeckFileKey?.message}
            onUploaded={(result) => {
              setValue('pitchDeckFileKey', result.fileKey, { shouldDirty: true, shouldValidate: true });
              setValue('pitchDeckFileSizeBytes', result.sizeBytes, { shouldDirty: true });
            }}
            onUploadingChange={trackUpload?.('pitchDeck')}
            onClear={() => {
              setValue('pitchDeckFileKey', '', { shouldDirty: true, shouldValidate: true });
              setValue('pitchDeckFileSizeBytes', undefined, { shouldDirty: true });
            }}
          />
        </div>
        {/* A link, not a dropzone (A-58). Sits outside the dropzone grid
            because it is a text field, and mirrors BasicDetails.tsx's URL
            inputs rather than its upload neighbours. */}
        <TextField
          {...register('pitchVideoUrl')}
          label={FIELD_LABELS.pitchVideo}
          type="url"
          placeholder="https://vimeo.com/your-pitch"
          error={errors.pitchVideoUrl?.message}
        />
      </div>
    </SectionCard>
  );
}
